import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { AUTH_TYPE, AuthOptions, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { inject, singleton } from 'tsyringe';
import { AuthenticationService } from '../../authentication/AuthenticationService';
import { GraphQlErrorHandler } from '../middleware/GraphQlErrorHandler';
import { ApiCacheService } from '../api-cache/ApiCacheService';
import { createCachingApolloLink } from '../api-cache/CachingApolloLink';

@singleton()
export class AmGraphQlClient extends ApolloClient<NormalizedCacheObject> {
  constructor(
    authenticationService: AuthenticationService,
    graphQlErrorHandler: GraphQlErrorHandler,
    apiCacheService: ApiCacheService,
    @inject('AmApiGraphqlHttpEndpoint') apiUrlHttp: string,
    @inject('AwsRegion') awsRegion: string
  ) {
    const auth: AuthOptions = {
      type: AUTH_TYPE.OPENID_CONNECT,
      jwtToken: async () => {
        const accessToken = (await authenticationService.getTokens())?.jwt.accessToken ?? 'Unauthorized';

        return accessToken;
      },
    };

    const httpLink = createHttpLink({ uri: apiUrlHttp });

    super({
      link: ApolloLink.from([
        createAuthLink({
          url: apiUrlHttp,
          region: awsRegion,
          auth,
        }),
        onError(graphQlErrorHandler.handle),
        createCachingApolloLink(apiCacheService, 'am'),
        createSubscriptionHandshakeLink(
          {
            url: apiUrlHttp,
            region: awsRegion,
            auth,
          },
          httpLink
        ),
      ]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        mutate: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      },
    });
  }
}
